import React, { useState, useEffect } from 'react';
import { Spin } from 'antd';
import { FiLock, FiUser, FiUsers, FiAlertTriangle, FiClock, FiXCircle } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import PasoHorarios    from './PasoHorarios';
import PasoPlan        from './PasoPlan';
import PasoDatos       from './PasoDatos';
import PasoConfirmacion from './PasoConfirmacion';

import { useNotify }        from '../../../providers/NotificationProvider';
import { useCart }          from '../../../providers/Cart';
import { useTarjetaVecino } from '../../../hooks/useTarjetaVecino';
import { useDescuentos }    from '../../../hooks/useDescuentos';
import MatriculaService     from '../../../service/Matriculas';
import dayjs from 'dayjs';

const TRAMO_MATRICULA = { particular: 'no vecino', vecino: 'vecino', adultoMayor: 'adulto mayor', funcionario: 'funcionario' };
const clp = (n) => `$${Math.round(n || 0).toLocaleString('es-CL')}`;

const STEP_META = [
  { icon: '🗓️', key: 'horarios',  label: 'Horarios'    },
  { icon: '💳', key: 'plan',      label: 'Plan'         },
  { icon: '👤', key: 'datos',     label: 'Tus datos'    },
  { icon: '✅', key: 'confirmar', label: 'Confirmación' },
];

export default function FormularioInscripcion({
  tallerId, taller, esGratis, user, familiares, loadingFamiliares,
  inscripcionForm, infoMatricula, estadoInscripcion
}) {
  const navigate = useNavigate();
  const notify   = useNotify();
  const { addToCart } = useCart();

  const [loading,               setLoading]               = useState(false);
  const [verificandoAcceso,     setVerificandoAcceso]     = useState(true);
  const [tieneAcceso,           setTieneAcceso]           = useState(false);
  const [tipoAcceso,            setTipoAcceso]            = useState(null);
  const [familiaresAutorizados, setFamiliaresAutorizados] = useState([]);
  const [validacionVecino,      setValidacionVecino]      = useState(false);
  const [preciosAplicados,      setPreciosAplicados]      = useState({});

  const tarjetaVecino = useTarjetaVecino();
  const { esVecino, estadoVecino, validando: validandoVecino, error: errorVecino } = tarjetaVecino;

  // Regla del descuento vecino (2026-07-07): aplica SOLO si el INSCRITO (el
  // participante que asiste) tiene TV vigente. El RUT del comprador/apoderado NO
  // cuenta. El backend es la fuente de verdad (ordenCompra/cotización); aquí
  // replicamos el criterio para el preview de precio y el carrito.
  const esVecinoEfectivo = esVecino;

  // El preview/carrito solo congela precios cuando la validación TV del inscrito
  // resolvió; si no, el descuento podría faltar por timing.
  const tvResuelta = !validandoVecino;

  const {
    form, currentStep, stepsConfig, getCurrentStepKey,
    selectedHorarios, toggleHorario,
    selectedPlan, setSelectedPlan,
    tipoInscripcion, setTipoInscripcion,
    selectedFamiliar, setSelectedFamiliar,
    nextStep, prevStep
  } = inscripcionForm;

  const descuentos = useDescuentos(tallerId, taller, esVecinoEfectivo);

  /* ── Validación tarjeta vecino ── */
  useEffect(() => {
    if (getCurrentStepKey() !== 'datos') { setValidacionVecino(false); return; }
    if (validandoVecino) { setValidacionVecino(false); return; }

    let rutAEvaluar = null;
    if (tipoInscripcion === 'para_mi') rutAEvaluar = user?.rut;
    else if (tipoInscripcion === 'para_familiar' && selectedFamiliar) {
      rutAEvaluar = familiares.find(f => f._id === selectedFamiliar)?.rut;
    }

    // Si no hay RUT que evaluar → no aplica validación, permitir avanzar.
    // Si hay RUT → exigir que la validación haya RESPONDIDO (estadoVecino !== null)
    // O que haya FALLADO (errorVecino): fail-open — si la API de TV está caída no
    // bloqueamos la inscripción, se continúa sin el descuento (el backend re-valida).
    setValidacionVecino(!rutAEvaluar || estadoVecino !== null || !!errorVecino);
  }, [validandoVecino, estadoVecino, errorVecino, getCurrentStepKey, tipoInscripcion, selectedFamiliar, user?.rut, familiares]);

  /* ── Verificar acceso academia ── */
  useEffect(() => {
    if (!taller || taller.tipoTaller !== 'academia') {
      setTieneAcceso(true); setTipoAcceso(null); setVerificandoAcceso(false); return;
    }
    if (!user?.sub) { setTieneAcceso(false); setTipoAcceso(null); setVerificandoAcceso(false); return; }

    const auth = taller.usuariosAutorizados || [];
    const direct = auth.find(a => a.tipo === 'usuario' && a.id === user.sub);
    if (direct) { setTieneAcceso(true); setTipoAcceso('usuario'); setVerificandoAcceso(false); return; }

    const famIds = familiares.map(f => f._id);
    const conAcceso = auth.filter(a => a.tipo === 'familiar' && famIds.includes(a.id));
    if (conAcceso.length > 0) {
      setTieneAcceso(true); setTipoAcceso('familiar');
      setFamiliaresAutorizados(conAcceso.map(f => f.id));
      setVerificandoAcceso(false); return;
    }
    setTieneAcceso(false); setTipoAcceso(null); setVerificandoAcceso(false);
    notify.error('No tienes autorización para esta academia');
  }, [taller, user, familiares, notify]);

  /* ── Cálculo de precios en paso confirmar ── */
  const getFecha = () => {
    if (tipoInscripcion === 'para_mi') return user?.birthdate;
    if (selectedFamiliar) return familiares.find(f => f._id === selectedFamiliar)?.fechaNacimiento;
    return form.getFieldValue('fechaNacimientoFamiliar');
  };
  useEffect(() => {
    if (getCurrentStepKey() !== 'confirmar') return;
    if (!tvResuelta) return; // esperar validación TV del inscrito para no perder el descuento
    const fecha = getFecha();
    if (fecha && descuentos.calcularPreciosAplicados)
      descuentos.calcularPreciosAplicados(fecha).then(setPreciosAplicados);
  }, [currentStep, tipoInscripcion, selectedFamiliar, esVecino, tvResuelta]);

  /* ── Matrícula de la persona inscrita (paso confirmar) ──
     Si el producto la exige: avisa si ya tiene una vigente o cuánto se sumará al
     pagar. Es informativo; el cobro lo decide la cotización en el backend. */
  const [estadoMatricula, setEstadoMatricula] = useState(null);
  useEffect(() => {
    if (!taller?.requiereMatricula || !infoMatricula || getCurrentStepKey() !== 'confirmar') {
      setEstadoMatricula(null);
      return undefined;
    }
    let rut;
    if (tipoInscripcion === 'para_mi') rut = user?.rut;
    else if (selectedFamiliar) rut = familiares.find(f => f._id === selectedFamiliar)?.rut;
    else rut = form.getFieldValue('rutFamiliar');
    if (!rut) return undefined;
    let vigente = true;
    MatriculaService.EstadoInscrito({ rut, recintoId: taller.recinto?._id || taller.recinto, fechaNacimiento: getFecha() })
      .then(e => { if (vigente) setEstadoMatricula(e); })
      .catch(() => { if (vigente) setEstadoMatricula(null); });
    return () => { vigente = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, tipoInscripcion, selectedFamiliar, infoMatricula]);

  /* ── Validación rango de edad ── */
  const validarEdad = (fecha) => {
    if (!fecha) return { valido: false, mensaje: 'Fecha de nacimiento inválida' };
    const edad = descuentos.calcularEdad(fecha);
    const { edadMin: mn, edadMax: mx } = taller;
    if (!mn && !mx) return { valido: true, edad };
    if (mn && edad < mn) return { valido: false, mensaje: `Edad mínima: ${mn} años (actual: ${edad} años)` };
    if (mx && edad > mx) return { valido: false, mensaje: `Edad máxima: ${mx} años (actual: ${edad} años)` };
    return { valido: true, edad };
  };

  /* ── handleNext ── */
  const MAX_HORARIOS = 5;
  const handleNext = () => {
    const key = getCurrentStepKey();
    if (key === 'horarios') {
      if (!selectedHorarios.length) { notify.warning('Selecciona al menos un horario'); return; }
      if (selectedHorarios.length > MAX_HORARIOS) {
        notify.warning(`Puedes seleccionar hasta ${MAX_HORARIOS} horarios por inscripción`); return;
      }
    }
    if (key === 'plan') {
      if (!selectedPlan) { notify.warning('Selecciona un plan de pago'); return; }
      // Evita un plan obsoleto (p.ej. tras cambiar de taller) que rompería el precio.
      if (!esGratis && !(taller?.modalidadPago || []).some(p => p.plan === selectedPlan)) {
        notify.error('El plan seleccionado ya no está disponible'); return;
      }
    }
    if (key === 'datos') {
      if (!validacionVecino) {
        notify.warning(validandoVecino ? 'Espera la validación de tarjeta vecino…' : 'Completa la validación antes de continuar');
        return;
      }
      const v = validarEdad(getFecha());
      if (!v.valido) { notify.error(v.mensaje); return; }
      if (taller.tipoTaller === 'academia' && tipoAcceso === 'familiar' && tipoInscripcion === 'para_mi') {
        notify.error('Solo puedes inscribir a familiares autorizados'); return;
      }
    }
    nextStep();
  };

  /* ── Agregar al carrito ── */
  const handleAgregarAlCarrito = async (values) => {
    try {
      setLoading(true);
      // No agregar al carrito con precios sin descuento vecino por timing.
      if (!esGratis && !tvResuelta) {
        notify.warning('Validando descuentos vecino… intenta de nuevo en un momento');
        return;
      }
      let fecha, inscritoData;
      if (tipoInscripcion === 'para_mi') {
        fecha = user?.birthdate;
        inscritoData = { esUsuarioPrincipal: true, nombre: user.name, rut: user.rut,
          fechaNacimiento: user.birthdate ? dayjs(user.birthdate).toISOString() : null,
          genero: user.gender || null, telefono: user.phone, email: user.email };
      } else if (selectedFamiliar) {
        const fam = familiares.find(f => f._id === selectedFamiliar);
        fecha = fam.fechaNacimiento;
        inscritoData = { esUsuarioPrincipal: false, familiarId: fam._id, nombre: fam.nombre, rut: fam.rut,
          fechaNacimiento: fam.fechaNacimiento, genero: fam.genero,
          telefono: fam.telefono || user.phone, email: fam.email || user.email };
      } else {
        fecha = values.fechaNacimientoFamiliar;
        inscritoData = { esUsuarioPrincipal: false, nombre: values.nombreFamiliar, rut: values.rutFamiliar,
          fechaNacimiento: values.fechaNacimientoFamiliar.toISOString(), genero: values.generoFamiliar,
          telefono: values.telefonoFamiliar || user.phone, email: user.email };
      }
      const v = validarEdad(fecha);
      if (!v.valido) { notify.error(v.mensaje); return; }

      for (const horario of selectedHorarios) {
        let plan;
        if (esGratis) {
          plan = { plan: 'gratuito', valor: 0, valorNormal: 0, tipoPrecio: 'gratuito' };
        } else {
          const pa = preciosAplicados[selectedPlan];
          if (!pa) { notify.error('No se pudo determinar el precio. Vuelve al paso anterior.'); return; }
          plan = { plan: selectedPlan, valor: pa.montoFinal, valorNormal: pa.montoOriginal, tipoPrecio: pa.tipo,
            descuentos: { esVecino: pa.esVecino, esAdultoMayor: pa.esAdultoMayor, tieneDescuento: pa.tieneDescuento,
              descuentoEspecial: pa.descuentoEspecial, descuentoAutomatico: pa.totalDescuentoAutomatico,
              descuentosAplicados: pa.descuentosAplicados, totalDescuento: pa.totalDescuento } };
        }
        await addToCart({ tallerId: taller._id, horarioId: horario._id, tipoInscripcion, inscritoData, planSeleccionado: plan });
      }
      notify.success(`${selectedHorarios.length} horario(s) agregado(s) al carrito`);
      navigate('/carrito');
    } catch (e) {
      notify.error(e.message || 'Error al agregar al carrito');
    } finally { setLoading(false); }
  };

  /* ── Renders bloqueantes ── */
  if (verificandoAcceso) return (
    <div className="nd-card p-10 flex flex-col items-center gap-3" id="formulario-inscripcion">
      <Spin size="large" />
      <p className="text-gray-400 text-sm">Verificando acceso…</p>
    </div>
  );

  if (taller.tipoTaller === 'academia' && !tieneAcceso) return (
    <div className="nd-card p-8 flex flex-col items-center text-center gap-4" id="formulario-inscripcion">
      <div className="w-16 h-16 rounded-full flex items-center justify-center text-3xl" style={{ background: '#fff7ed' }}>🔒</div>
      <h3 className="font-extrabold text-gray-900 text-lg">Acceso restringido</h3>
      <p className="text-gray-500 text-sm max-w-sm">Esta academia es de acceso controlado. Contacta al administrador del recinto si crees que deberías tener acceso.</p>
    </div>
  );

  if (!estadoInscripcion?.mostrarFormulario) {
    const t = estadoInscripcion?.tipo;
    return (
      <div className={`nd-card p-8 flex flex-col items-center text-center gap-4 border-2 ${t === 'proximo' ? 'border-blue-200' : t === 'cerrado' ? 'border-orange-200' : 'border-red-200'}`} id="formulario-inscripcion">
        <span className="text-4xl">{t === 'proximo' ? '⏳' : t === 'cerrado' ? '🚫' : '⚠️'}</span>
        <h3 className={`font-extrabold text-lg ${t === 'proximo' ? 'text-blue-700' : t === 'cerrado' ? 'text-orange-700' : 'text-red-700'}`}>
          {t === 'proximo' ? 'Inscripciones próximas' : t === 'cerrado' ? 'Inscripciones cerradas' : 'No disponible'}
        </h3>
        <p className="text-sm text-gray-500 max-w-sm">{estadoInscripcion?.mensaje}</p>
      </div>
    );
  }

  /* ── Render principal ── */
  const total    = stepsConfig.length;
  const progress = ((currentStep + 1) / total) * 100;
  const stepMeta = STEP_META[currentStep] || STEP_META[0];

  return (
    <div id="formulario-inscripcion" className="nd-card overflow-hidden">

      {/* Header con progreso */}
      <div className="px-5 pt-5 pb-4"
        style={{ background: 'linear-gradient(135deg,#00266b 0%,#003a94 50%,#0047bb 100%)' }}>

        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-white font-extrabold text-lg leading-none">Inscripción</h3>
              {taller.tipoTaller === 'academia' && (
                <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-white/15 text-white border border-white/20">🏛️ Academia</span>
              )}
              {taller.requiereMatricula && estadoMatricula?.tieneMatricula && (
                <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-white/15 text-white border border-white/20">✓ Matrícula</span>
              )}
            </div>
            <p className="text-white/60 text-[12px] mt-1">
              Paso {currentStep + 1} de {total} · <span className="text-white font-semibold">{stepsConfig[currentStep]?.title}</span>
            </p>
          </div>
          <span className="text-3xl leading-none">{stepMeta.icon}</span>
        </div>

        {/* Progress bar */}
        <div className="h-1.5 rounded-full bg-white/20 overflow-hidden mb-3">
          <div className="h-full rounded-full bg-white transition-all duration-700 ease-out" style={{ width: `${progress}%` }} />
        </div>

        {/* Step dots */}
        <div className="flex items-center justify-center gap-1.5">
          {stepsConfig.map((_, i) => (
            <div key={i} className={`step-dot ${i === currentStep ? 'active' : i < currentStep ? 'done' : 'pending'}`} />
          ))}
        </div>
      </div>

      {/* Academia acceso OK */}
      {taller.tipoTaller === 'academia' && tieneAcceso && (
        <div className="mx-5 mt-4 flex items-center gap-3 p-3.5 rounded-2xl bg-blue-50 border border-blue-200">
          {tipoAcceso === 'usuario' ? <FiUser size={16} className="text-blue-600" /> : <FiUsers size={16} className="text-blue-600" />}
          <div>
            <p className="text-sm font-bold text-blue-800">
              {tipoAcceso === 'usuario' ? 'Acceso verificado' : `${familiaresAutorizados.length} familiar(es) autorizado(s)`}
            </p>
            <p className="text-[12px] text-blue-600">
              {tipoAcceso === 'usuario' ? 'Puedes inscribirte directamente' : 'Puedes inscribir a tus familiares autorizados'}
            </p>
          </div>
        </div>
      )}

      {/* Contenido del paso */}
      <div className="p-5">
        {getCurrentStepKey() === 'horarios' && (
          <PasoHorarios taller={taller} esGratis={esGratis} selectedHorarios={selectedHorarios}
            toggleHorario={toggleHorario} descuentosDisponibles={descuentos.descuentosDisponibles} onNext={handleNext} />
        )}
        {getCurrentStepKey() === 'plan' && !esGratis && (
          <PasoPlan taller={taller} selectedPlan={selectedPlan} setSelectedPlan={setSelectedPlan}
            selectedHorarios={selectedHorarios} preciosDisponibles={descuentos.preciosConDescuento}
            loadingDescuentos={descuentos.loading} esVecino={esVecinoEfectivo} onNext={handleNext} onPrev={prevStep} />
        )}
        {getCurrentStepKey() === 'datos' && (
          <PasoDatos form={form} user={user} familiares={familiares} loadingFamiliares={loadingFamiliares}
            tipoInscripcion={tipoInscripcion} setTipoInscripcion={setTipoInscripcion}
            selectedFamiliar={selectedFamiliar} setSelectedFamiliar={setSelectedFamiliar}
            taller={taller} selectedHorarios={selectedHorarios}
            validarRangoEdad={validarEdad} calcularEdad={descuentos.calcularEdad}
            tarjetaVecino={tarjetaVecino} tipoAcceso={tipoAcceso}
            familiaresAutorizados={familiaresAutorizados}
            validacionVecinoCompletada={validacionVecino} onNext={handleNext} onPrev={prevStep} />
        )}
        {getCurrentStepKey() === 'confirmar' && estadoMatricula?.requiereMatricula && (
          estadoMatricula.tieneMatricula ? (
            <div className="mb-4 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
              <strong>✓ Ya tiene matrícula vigente</strong> en {estadoMatricula.recintoNombre}
              {estadoMatricula.venceEl ? ` (hasta el ${dayjs(estadoMatricula.venceEl).format('DD/MM/YYYY')})` : ''}. No se cobra matrícula.
            </div>
          ) : (
            <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <strong>Se sumará la matrícula</strong> de {estadoMatricula.recintoNombre}
              {estadoMatricula.textoVigencia ? ` (${estadoMatricula.textoVigencia.toLowerCase()})` : ''}:{' '}
              <strong>{estadoMatricula.precio > 0 ? clp(estadoMatricula.precio) : 'gratis'}</strong>
              {estadoMatricula.tipoPrecio ? ` · valor ${TRAMO_MATRICULA[estadoMatricula.tipoPrecio] || estadoMatricula.tipoPrecio}` : ''}.
              {' '}Se cobra al pagar, junto con el producto.
              {estadoMatricula.vencida && <span className="block text-xs mt-1">Su matrícula anterior está vencida.</span>}
            </div>
          )
        )}
        {getCurrentStepKey() === 'confirmar' && (
          <PasoConfirmacion form={form} taller={taller} esGratis={esGratis} user={user}
            familiares={familiares} selectedHorarios={selectedHorarios} selectedPlan={selectedPlan}
            tipoInscripcion={tipoInscripcion} selectedFamiliar={selectedFamiliar}
            preciosAplicados={preciosAplicados} calcularEdad={descuentos.calcularEdad}
            loading={loading} esVecino={esVecinoEfectivo} estadoVecino={estadoVecino}
            validandoTV={!tvResuelta}
            onSubmit={handleAgregarAlCarrito} onPrev={prevStep} />
        )}
      </div>
    </div>
  );
}
